<script lang="ts" setup>
import isIos from 'is-ios'
import { BarMitzvahType } from 'types/types'
import { computed, ref, watch } from 'vue'

import { type NewlineBrushKind } from '@/composables/useTextColoration'
import { FONT_SIZE_LARGE, FONT_SIZE_XLARGE } from '@/constants/annotationConstants'

type TabValue = 'color' | 'format' | 'newline'

interface Props {
  active: boolean
  barMitzvah: BarMitzvahType | null
  backgroundColor: string
  // 'break' brush inserts a line break (newlineAfter); 'gap' inserts a setuma gap.
  brushKind?: NewlineBrushKind
  // Column-overlay edge trim (only meaningful in column mode).
  cutTrailingWords?: number
  fontSize: string
  foregroundColor: string
  hasSelection?: boolean
  isColumnMode?: boolean
  newlineBrushActive?: boolean
  skipPrefixWords?: number
  totalWords?: number
  underline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  brushKind: 'break',
  cutTrailingWords: 0,
  hasSelection: false,
  isColumnMode: false,
  newlineBrushActive: false,
  skipPrefixWords: 0,
  totalWords: 0,
  underline: false,
})

// The column must keep >= 1 word visible, so a word can be hidden at either edge
// only while more than one word remains. Revealing is bounded by what's hidden.
const canHideWord = computed(() => props.totalWords - props.skipPrefixWords - props.cutTrailingWords > 1)

interface Emits {
  // edge: 'start' trims the first visible verse, 'end' the last; delta +1 hides
  // one more word, -1 reveals one more.
  (e: 'adjustTrim', edge: 'start' | 'end', delta: number): void
  (e: 'autoColor'): void
  (e: 'clearAllColors'): void
  (e: 'clearAllWhitespace'): void
  (e: 'clearSelection'): void
  (e: 'close'): void
  (e: 'setBrushKind', kind: NewlineBrushKind): void
  (e: 'toggleNewlineBrush'): void
  (e: 'updateBackground', color: string): void
  (e: 'updateFontSize', size: string): void
  (e: 'updateForeground', color: string): void
  (e: 'updateUnderline', value: boolean): void
}

const emit = defineEmits<Emits>()

// Initialize from the prop: the toolbar can mount with the brush already active
// (e.g. auto-edit after upload), and the watch below only reacts to later changes.
const activeTab = ref<TabValue>(props.newlineBrushActive ? 'newline' : 'color')

// Sync activeTab when parent toggles newlineBrushActive externally
watch(
  () => props.newlineBrushActive,
  (active) => {
    if (active && activeTab.value !== 'newline') {
      activeTab.value = 'newline'
    } else if (!active && activeTab.value === 'newline') {
      activeTab.value = 'color'
    }
  },
)

// Reset to Colors tab when toolbar is deactivated
watch(
  () => props.active,
  (active) => {
    if (!active) {
      activeTab.value = 'color'
    }
  },
)

function onTabChange(tab: TabValue) {
  const wasNewline = activeTab.value === 'newline'
  const isNewline = tab === 'newline'
  activeTab.value = tab
  // Toggle the composable's newline brush when entering/leaving the newline tab
  if (wasNewline !== isNewline) {
    emit('toggleNewlineBrush')
  }
}
</script>

<template>
  <v-card v-if="active && barMitzvah && !isIos" class="coloration-toolbar mb-4" :ripple="false" variant="outlined">
    <v-btn class="toolbar-close" density="compact" icon size="small" variant="text" @click="emit('close')">
      <v-icon size="small">mdi-close</v-icon>
    </v-btn>

    <div class="toolbar-body">
      <div class="toolbar-mode-toggle mb-2 mr-6">
        <v-btn-toggle
          color="primary"
          density="compact"
          mandatory
          :model-value="activeTab"
          variant="outlined"
          @update:model-value="onTabChange"
        >
          <v-btn size="small" value="color">
            <v-icon size="small" start>mdi-palette</v-icon>
            Colors
          </v-btn>
          <v-btn size="small" value="format">
            <v-icon size="small" start>mdi-format-size</v-icon>
            Format
          </v-btn>
          <v-btn size="small" value="newline">
            <v-icon size="small" start>mdi-format-line-spacing</v-icon>
            {{ props.isColumnMode ? 'Column' : 'Newline' }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <!-- Color tab -->
      <template v-if="activeTab === 'color'">
        <div class="toolbar-pickers">
          <div>
            <span class="text-caption text-medium-emphasis">Foreground</span>
            <compact-picker :model-value="foregroundColor" @update:model-value="emit('updateForeground', $event)" />
          </div>
          <div>
            <span class="text-caption text-medium-emphasis">Background</span>
            <compact-picker :model-value="backgroundColor" @update:model-value="emit('updateBackground', $event)" />
          </div>
        </div>

        <div class="toolbar-actions">
          <v-btn :disabled="!props.hasSelection" size="small" variant="outlined" @click="emit('clearSelection')">Clear color</v-btn>
          <v-btn color="warning" size="small" variant="outlined" @click="emit('clearAllColors')">Clear all</v-btn>
          <v-spacer />
          <v-btn size="small" variant="outlined" @click="emit('autoColor')">
            <v-icon size="small" start>mdi-brush</v-icon>
            Special nikkud
          </v-btn>
        </div>
      </template>

      <!-- Format tab -->
      <template v-else-if="activeTab === 'format'">
        <div>
          <span class="text-caption text-medium-emphasis">Size</span>
          <div class="d-flex ga-2 mt-1">
            <v-btn
              :active="!props.fontSize"
              :disabled="!props.hasSelection"
              size="small"
              variant="outlined"
              @click="emit('updateFontSize', '')"
            >
              Normal
            </v-btn>
            <v-btn
              :active="props.fontSize === FONT_SIZE_LARGE"
              :disabled="!props.hasSelection"
              size="small"
              variant="outlined"
              @click="emit('updateFontSize', FONT_SIZE_LARGE)"
            >
              Large
            </v-btn>
            <v-btn
              :active="props.fontSize === FONT_SIZE_XLARGE"
              :disabled="!props.hasSelection"
              size="small"
              variant="outlined"
              @click="emit('updateFontSize', FONT_SIZE_XLARGE)"
            >
              X-Large
            </v-btn>
          </div>
        </div>
        <div class="mt-2">
          <span class="text-caption text-medium-emphasis">Style</span>
          <div class="d-flex ga-2 mt-1">
            <v-btn
              :active="props.underline"
              :disabled="!props.hasSelection"
              size="small"
              variant="outlined"
              @click="emit('updateUnderline', !props.underline)"
            >
              <v-icon size="small" start>mdi-format-underline</v-icon>
              Underline
            </v-btn>
          </div>
        </div>
        <div class="toolbar-actions">
          <v-btn :disabled="!props.hasSelection" size="small" variant="outlined" @click="emit('clearSelection')">Clear format</v-btn>
          <v-btn color="warning" size="small" variant="outlined" @click="emit('clearAllColors')">Clear all</v-btn>
        </div>
      </template>

      <!-- Newline tab -->
      <template v-else>
        <v-btn-toggle
          color="primary"
          density="compact"
          mandatory
          :model-value="props.brushKind"
          variant="outlined"
          @update:model-value="(kind: NewlineBrushKind) => emit('setBrushKind', kind)"
        >
          <v-btn size="small" value="break">
            <v-icon size="small" start>mdi-format-line-spacing</v-icon>
            Line break
          </v-btn>
          <v-btn size="small" value="gap">
            <v-icon size="small" start>mdi-arrow-expand-horizontal</v-icon>
            Gap
          </v-btn>
        </v-btn-toggle>
        <div class="text-caption text-medium-emphasis">
          {{
            props.brushKind === 'gap'
              ? 'Tap a word to insert or remove a stretching gap (setuma) after it.'
              : 'Tap a word to insert or remove a line break after it.'
          }}
        </div>
        <div v-if="props.isColumnMode" class="mt-1">
          <span class="text-caption text-medium-emphasis">Pull words in or out at the column edges</span>
          <div class="d-flex ga-6 mt-1">
            <div class="d-flex align-center ga-1">
              <span class="text-caption mr-1">Start</span>
              <v-btn
                aria-label="Reveal a word at the start"
                class="trim-start-reveal"
                density="comfortable"
                icon
                size="x-small"
                variant="outlined"
                @click="emit('adjustTrim', 'start', -1)"
                ><v-icon size="small">mdi-plus</v-icon></v-btn
              >
              <v-btn
                aria-label="Hide a word at the start"
                class="trim-start-hide"
                density="comfortable"
                :disabled="!canHideWord"
                icon
                size="x-small"
                variant="outlined"
                @click="emit('adjustTrim', 'start', 1)"
                ><v-icon size="small">mdi-minus</v-icon></v-btn
              >
            </div>
            <div class="d-flex align-center ga-1">
              <span class="text-caption mr-1">End</span>
              <v-btn
                aria-label="Reveal a word at the end"
                class="trim-end-reveal"
                density="comfortable"
                icon
                size="x-small"
                variant="outlined"
                @click="emit('adjustTrim', 'end', -1)"
                ><v-icon size="small">mdi-plus</v-icon></v-btn
              >
              <v-btn
                aria-label="Hide a word at the end"
                class="trim-end-hide"
                density="comfortable"
                :disabled="!canHideWord"
                icon
                size="x-small"
                variant="outlined"
                @click="emit('adjustTrim', 'end', 1)"
                ><v-icon size="small">mdi-minus</v-icon></v-btn
              >
            </div>
          </div>
        </div>
        <div class="toolbar-actions">
          <v-btn color="warning" size="small" variant="outlined" @click="emit('clearAllWhitespace')">Clear all</v-btn>
        </div>
      </template>
    </div>
  </v-card>
</template>

<style scoped>
.coloration-toolbar {
  display: flex;
  background: rgb(var(--v-theme-surface));
}

.toolbar-close {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
}

.toolbar-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
}

.toolbar-pickers {
  display: flex;
  gap: 20px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
